<template>
  <div class="sidebar">
    <div class="sidebar_top">
      <div class="logo_container">
        <Brand />
      </div>
      <button type="button" class="search_button" @click="showSearch = true">
        <span class="material-symbols-outlined left">search</span>
        <span class="search_label">{{ $t("sidebar.search") }}</span>
        <span class="search_shortcut">{{ isMac ? "⌘K" : "Ctrl+K" }}</span>
      </button>
    </div>

    <div class="sidebar_middle">
      <HcpInboxNavigation />
      <template v-if="workspaces && workspaces.length > 0">
        <div class="menu_item">
          <h3
            :class="{
              title: true,
              active: isHighlightedWorkspace(
                store.getters.currentWorkspace,
                true,
              ),
            }"
          >
            <div class="hover_container">
              <span class="material-symbols-outlined hide_hover left">
                folder
              </span>

              <button
                type="button"
                class="sidebar_icon_button material-symbols-outlined display_hover left"
                data-test="workspace-expander-ctrl"
                :aria-label="$t('sidebar.workspaces.title')"
                :aria-expanded="workspacesExpanded"
                @click.stop="toggleWorkspaces"
              >
                {{
                  workspacesExpanded ? "keyboard_arrow_down" : "chevron_right"
                }}
              </button>

              <router-link to="/u/workspaces">
                {{ $t("sidebar.workspaces.title") }}
              </router-link>
            </div>

            <tooltip v-if="canCreateWorkspace" class="right bottom">
              <template v-slot>
                <button
                  type="button"
                  class="sidebar_icon_button material-symbols-outlined"
                  :aria-label="$t('sidebar.createWorkspace')"
                  @click.stop="displayCreateWorkspaceModal = true"
                  data-test="create-workspace-ctrl"
                >
                  add
                </button>
              </template>
              <template #tooltip>{{ $t("sidebar.createWorkspace") }}</template>
            </tooltip>
          </h3>

          <div class="items" v-if="workspacesExpanded">
            <router-link
              :class="{
                item: true,
                active: isHighlightedWorkspace(workspace.id),
              }"
              v-for="workspace in workspaces"
              :key="workspace.id"
              :to="{ name: 'Workspace', params: { workspaceId: workspace.id } }"
              :data-test="`workspace-item-${workspace.name}`"
            >
              {{ workspace.name }}
            </router-link>
          </div>
        </div>
      </template>

      <div v-if="canUseSharedSpaces" class="menu_item">
        <h3
          :class="{
            title: true,
            active: spacesIsActive,
          }"
        >
          <span class="material-symbols-outlined left"> attach_file </span>

          <router-link to="/u/shared_spaces">
            {{ $t("sidebar.documentSpaces.title") }}
          </router-link>

          <tooltip class="right">
            <template v-slot>
              <button
                type="button"
                class="sidebar_icon_button material-symbols-outlined"
                data-test="sidebar-create-shared-space-ctrl"
                :aria-label="$t('sidebar.createSharedSpace')"
                @click.stop="handleModalDisplay('create')"
              >
                add
              </button>
            </template>
            <template #tooltip>{{ $t("sidebar.createSharedSpace") }}</template>
          </tooltip>
        </h3>
      </div>

      <template v-if="hasCustomFlows">
        <div class="menu_item">
          <h3
            :class="{
              title: true,
              active: flowIsActive,
            }"
          >
            <span class="material-symbols-outlined left"> layers </span>
            <router-link :to="{ name: 'CustomFlows' }">
              {{ $t("sidebar.customFlows.title") }}
            </router-link>
          </h3>
        </div>
      </template>

      <template v-if="hasAnalytics">
        <div class="menu_item">
          <h3
            :class="{
              title: true,
              active: analyticsIsActive,
            }"
          >
            <span class="material-symbols-outlined left"> insert_chart </span>
            <router-link :to="{ name: 'AnalyticsOverview' }">
              {{ $t("sidebar.analytics.title") }}
            </router-link>
          </h3>
        </div>
      </template>

      <template v-if="hasSignConsent">
        <div class="menu_item">
          <h3
            :class="{
              title: true,
              active: crmIsActive,
            }"
          >
            <span class="material-symbols-outlined left">
              import_contacts
            </span>
            <router-link :to="{ name: 'CRMContacts' }">
              {{ $t("sidebar.crmContacts.title") }}
            </router-link>
          </h3>
        </div>
      </template>
    </div>

    <div class="sidebar_bottom">
      <div v-if="customerTicketsEnabled" class="menu_item">
        <h3 :class="{ title: true, active: route.name === 'Tickets' }">
          <span class="material-symbols-outlined left" aria-hidden="true"
            >confirmation_number</span
          >
          <router-link :to="{ name: 'Tickets' }">{{
            $t("tickets.navigation")
          }}</router-link>
        </h3>
      </div>
      <template v-if="showMessagesEntry">
        <div class="menu_item">
          <h3 :class="{ title: true, active: messagesIsActive }">
            <span class="material-symbols-outlined left"> mail </span>
            <router-link :to="{ name: 'Messages' }">
              {{ $t("sidebar.messages.title") }}
            </router-link>
          </h3>
        </div>
      </template>

      <div class="menu_item">
        <h3 :class="{ title: true, active: settingsIsActive }">
          <span class="material-symbols-outlined left"> settings </span>
          <router-link to="/u/settings">
            {{ $t("sidebar.settings.title") }}
          </router-link>
        </h3>
      </div>

      <div class="menu_item">
        <h3 class="title">
          <dropdown class="top" :items="userItems" @change="handleAction">
            <template v-slot>
              <div class="avatar_container">
                <user-avatar
                  class="small"
                  :email="store.getters.user.email"
                  :name="store.getters.user.name"
                  :profileImgUrl="
                    getStorageProxyUrl(store.getters.user.profileImgUrl)
                  "
                />
                <span>{{ userName }}</span>
              </div>
            </template>
          </dropdown>
        </h3>
      </div>
    </div>
  </div>

  <create-workspace-modal
    v-if="displayCreateWorkspaceModal"
    @close="displayCreateWorkspaceModal = false"
    @success="redirectToWorkspace"
  />

  <create-document-space-modal
    v-if="createDocumentSpaceModalVisible"
    :inherit-classic-theme="inheritClassicRouteTheme"
    @close="displayCreateDocumentSpaceModal = false"
  />

  <upgrade-modal
    v-if="upgradeModalVisible"
    :inherit-classic-theme="inheritClassicRouteTheme"
    @close="displayUpgradeModal = false"
  />

  <OverlayModal v-if="showSearch" @close="showSearch = false">
    <GlobalSearch @close="showSearch = false" />
  </OverlayModal>
</template>

<script lang="ts" setup>
import HcpInboxNavigation from "@/hcp/HcpInboxNavigation.vue";
import { isOrdinaryWorkspace } from "@/hcp/inbox";
import {
  Dropdown,
  CreateWorkspaceModal,
  Tooltip,
  Brand,
  UserAvatar,
  CreateDocumentSpaceModal,
  UpgradeModal,
} from "@/components";
import SearchLoadingModal from "@/components/SearchLoadingModal.vue";
import { Workspace, useMyAdminTeamsShortQuery } from "@/graphql/operations";
import {
  computed,
  ComputedRef,
  defineAsyncComponent,
  defineComponent,
  h,
  nextTick,
  ref,
  onMounted,
  onUnmounted,
  watch,
} from "vue";
import { auth } from "@/fire";
import { signOut } from "firebase/auth";
import { useStore } from "vuex";
import { useRouter, useRoute } from "vue-router";
import { dropUserIdFromSession, token } from "@/init";
import {
  customerTicketsEnabled,
  refreshCustomerTicketsAccess,
} from "@/views/tickets/ticketsAccess";
import { useUser } from "@/util/useUser";
import { useSessionStorage } from "@vueuse/core";
import { useSpaceModalDisplay } from "@/composables";
import { getStorageProxyUrl } from "@/util";
import { useI18n } from "vue-i18n";
import { hasAnyActiveAdminTeam } from "@/views/messages/messagesAccess";

const {
  displayCreateDocumentSpaceModal,
  displayUpgradeModal,
  createDocumentSpaceModalVisible,
  upgradeModalVisible,
  handleModalDisplay,
} = useSpaceModalDisplay();

const SearchLoadingContent = defineComponent({
  name: "SearchLoadingContent",
  setup() {
    const { t } = useI18n();

    return () =>
      h(
        "div",
        {
          style: {
            alignItems: "center",
            color: "var(--classic-text-muted, #6b7280)",
            display: "flex",
            flex: "1",
            fontSize: "14px",
            justifyContent: "center",
            padding: "24px",
          },
        },
        t("sharedUi.common.loading"),
      );
  },
});

const OverlayModal = defineAsyncComponent({
  loader: () => import("@/components/common/OverlayModal.vue"),
  loadingComponent: SearchLoadingModal,
  delay: 0,
});

const GlobalSearch = defineAsyncComponent({
  loader: () => import("@/components/search/GlobalSearch.vue"),
  loadingComponent: SearchLoadingContent,
  delay: 0,
});

const user = useUser();
const userShouldSignInWithSso = computed(
  () =>
    user.value?.tenant?.hasExternalGroups &&
    user.value.externalGroupsSyncedAt === null,
);
const skipSsoForThisSession = useSessionStorage(
  "skipSsoForThisSession",
  "false",
  { listenToStorageChanges: true },
);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const route = useRoute();
watch(
  [token, () => store.getters.user?.tenantId, () => route.fullPath],
  () => refreshCustomerTicketsAccess(token.value),
  { immediate: true },
);

// Sidebar survives child-route navigation; abandon its creator with that route.
watch(
  () => route.fullPath,
  () => {
    displayCreateDocumentSpaceModal.value = false;
  },
);
const { data: myAdminTeamsData } = useMyAdminTeamsShortQuery({
  requestPolicy: "cache-and-network",
});

const workspaces: ComputedRef<Array<Workspace>> = computed(() =>
  store.getters.rootWorkspaces.filter(isOrdinaryWorkspace),
);

const isHighlightedWorkspace = (
  workspaceId: string | null,
  header: boolean = false,
) => {
  if (
    !route.matched.some(({ path }: { path: string }) =>
      /^\/u\/:workspaceId(\/.*)?$/i.test(path),
    )
  ) {
    return false;
  }

  if (header) {
    return (
      (workspacesIsActive.value && !store.getters.currentWorkspace) ||
      (store.getters.currentWorkspace && !workspacesExpanded.value)
    );
  }

  if (!store.getters.currentWorkspace) {
    return false;
  }

  return (
    workspaceId === store.getters.currentWorkspace.id ||
    workspaceId === store.getters.currentWorkspace?.root?.id
  );
};

const workspacesExpanded = ref(
  localStorage.getItem("workspacesExpanded") === "false" ? false : true,
);
const toggleWorkspaces = () => {
  workspacesExpanded.value = !workspacesExpanded.value;
  localStorage.setItem(
    "workspacesExpanded",
    workspacesExpanded.value.toString(),
  );
};

const userName = computed(
  () => store.getters.user.name || store.getters.user.email,
);
const userItems = computed(() => [
  {
    value: "Profile",
    title: t("sidebar.userMenu.profile"),
  },
  {
    value: "logout",
    title: t("sidebar.userMenu.logout"),
  },
]);

const workspacesIsActive = computed(() =>
  route.matched.some(({ path }: { path: string }) =>
    /^\/u\/workspaces/i.test(path),
  ),
);

const settingsIsActive = computed(() =>
  route.matched.some(({ path }: { path: string }) =>
    /^\/u\/settings/i.test(path),
  ),
);

const messagesIsActive = computed(() => /^\/u\/messages/i.test(route.path));

const spacesIsActive = computed(() =>
  route.matched.some(({ path }: { path: string }) =>
    /^\/u\/shared_spaces/i.test(path),
  ),
);

const inheritClassicRouteTheme = computed(
  () => route.meta.classicTheme === true,
);

const analyticsIsActive = computed(() =>
  route.matched.some(({ path }: { path: string }) =>
    /^\/u\/analytics/i.test(path),
  ),
);

const flowIsActive = computed(() =>
  route.matched.some(({ path }: { path: string }) =>
    /^\/u\/custom_flows/i.test(path),
  ),
);

const crmIsActive = computed(() =>
  route.matched.some(({ path }: { path: string }) => /^\/u\/crm/i.test(path)),
);

const hasCustomFlows = computed(() => store.getters.user.hasCustomFlows());
const canUseSharedSpaces = computed(() =>
  store.getters.user.canUseSharedSpaces(),
);
const showMessagesEntry = computed(() =>
  hasAnyActiveAdminTeam(myAdminTeamsData.value?.myAdminTeams?.nodes),
);

const canCreateWorkspace = computed(() =>
  store.getters.user.canCreateWorkspace(),
);

const hasAnalytics = computed(() => store.getters.user.hasAnalytics());

const hasSignConsent = computed(() => store.getters.user.hasSignConsent());

async function logout() {
  try {
    await Promise.all([signOut(auth), dropUserIdFromSession()]);
  } catch (e) {
    throw e;
  } finally {
    const path = router.resolve("/login");
    window.location.replace(path.href);
  }
}

async function handleAction(action: string) {
  switch (action) {
    case "logout":
      await logout();
      break;

    default:
      router.push({ name: action });
      break;
  }
}

const redirectToWorkspace = (id: number) => {
  router.push({
    name: "Workspace",
    params: { workspaceId: id },
  });
};

const displayCreateWorkspaceModal = ref<boolean>(false);

const showSearch = ref(false);
let searchReturnFocus: HTMLElement | null = null;
watch(
  showSearch,
  async (visible) => {
    if (visible) {
      searchReturnFocus =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
    } else {
      const target = searchReturnFocus;
      searchReturnFocus = null;
      await nextTick();
      if (!showSearch.value && target?.isConnected) target.focus();
    }
  },
  { flush: "sync" },
);
const isMac = /mac/i.test(
  (navigator as any).userAgentData?.platform ?? navigator.platform,
);

function handleSearchShortcut(e: KeyboardEvent) {
  if ((e.metaKey || e.ctrlKey) && e.key === "k") {
    e.preventDefault();
    showSearch.value = !showSearch.value;
  }
}

onMounted(() => {
  document.addEventListener("keydown", handleSearchShortcut, { capture: true });
});

onUnmounted(() => {
  document.removeEventListener("keydown", handleSearchShortcut, {
    capture: true,
  });
});
</script>

<style lang="scss" scoped>
.sidebar {
  width: $sidebar-width;
  flex-shrink: 0;
  height: 100vh;
  background: var(--classic-surface-raised, $background-grey);
  border-right: 1px solid var(--classic-border, #e0e0e0);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.sidebar_middle {
  flex-grow: 1;
  overflow-y: auto;
}

.sidebar_bottom {
  margin-top: auto;
  padding-bottom: 10px;
}

.logo_container {
  padding: 1em 1.2em;
  margin-bottom: 0.5em;
}

.search_button {
  font: inherit;
  display: flex;
  align-items: center;
  margin: 0 0.8em 0.8em;
  padding: 0.45em 0.7em;
  border-radius: 6px;
  border: 1px solid var(--classic-border, #e0e0e0);
  cursor: pointer;
  font-size: 0.85em;
  color: var(--classic-text-muted, #888);
  background: var(--classic-surface, white);
  box-sizing: border-box;
  font-family: inherit;
  text-align: left;
  width: calc(100% - 1.6em);
  transition:
    border-color 0.15s,
    box-shadow 0.15s;

  &:hover {
    border-color: var(--classic-border-strong, #bbb);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  }

  .material-symbols-outlined {
    font-size: 1.2em;
    margin-right: 0.4em;
  }

  .search_label {
    flex: 1;
  }

  .search_shortcut {
    font-size: 0.8em;
    color: var(--classic-text-muted, #aaa);
    background: var(--classic-surface-inset, #f5f5f5);
    border: 1px solid var(--classic-border, #e0e0e0);
    border-radius: 4px;
    padding: 0.1em 0.4em;
    line-height: 1.4;
  }
}

.sidebar_icon_button {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  margin: 0;
  padding: 0;
}

.sidebar .title {
  font-size: 0.9em;
  font-weight: 500;
  text-decoration: none;
  display: flex;
  line-height: 3.1em;
  padding: 0 1.2em;
  align-items: center;
  margin: 0.1em 0;

  a {
    text-decoration: none;
    color: var(--classic-text, #363636);
    display: flex;
    align-items: center;
    width: 100%;
  }
}

.hover_container {
  display: flex;
  align-items: center;
  position: relative;
}

.hover_container .display_hover {
  position: absolute;
  inset-inline-start: 0;
  top: 50%;
  transform: translateY(-50%);
  opacity: 0;
  pointer-events: none;
}

.hover_container:hover .hide_hover,
.hover_container:focus-within .hide_hover {
  visibility: hidden;
}

.hover_container:hover .display_hover,
.hover_container:focus-within .display_hover {
  opacity: 1;
  pointer-events: auto;
}

section .menu_item:first-child {
  padding-top: 0;
}

.items {
  font-size: 0.9em;
  margin-bottom: 1em;
}

.item {
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  margin: 0.1em 0;
}

.item,
a.item {
  display: block;
  line-height: 2.3em;
  text-decoration: none;
  color: var(--classic-text, #363636);
  padding-left: 45px;
  padding-right: 5px;
}

a.item.underline {
  text-decoration: underline;
}

.sidebar .title:hover,
.item:hover {
  cursor: pointer;
  background-color: var(--classic-surface-inset, #ececec);
}

.sidebar .title.active,
.item.active {
  background-color: var(--classic-surface-inset, #ececec);
}

.link {
  font-size: 0.9em;
  color: initial;
}

.material-symbols-outlined {
  font-size: 1.4em;
}

.material-symbols-outlined.left {
  margin-right: 0.3em;
}

.tooltip_container.right {
  margin-left: auto;
}

.tooltip_container .material-symbols-outlined {
  font-size: 1.3em;
}

.tooltip_container .material-symbols-outlined:hover {
  cursor: pointer;
}

.avatar {
  margin-right: 0.3em;
}

.avatar_container {
  display: flex;
  align-items: center;
}

section.user_info {
  font-size: 0.9em;
  line-height: 2.2em;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.user_name {
  width: calc(100% - 31px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.user_info .material-symbols-outlined {
  font-size: 1.3em;
  color: $color-secondary;
  border: 1px solid $color-secondary;
  border-radius: 50%;
  padding: 0.2em;
}

.icon.right {
  margin-left: auto;
}
</style>
